import { type RefObject, useEffect } from 'react';

/** Pointer travel before a press on empty board becomes a pan (a plain click stays a click). */
export const PAN_THRESHOLD = 5;

/** Things a press on must never start a pan: they have their own behaviour. */
const INTERACTIVE =
  'a, button, input, textarea, select, label, summary, [role="button"], [role="menu"], [role="dialog"], [draggable="true"], [data-no-pan], [contenteditable="true"]';

/** Whether a press at `target` (inside the scroller) may start a pan. */
export function canStartPan(target: EventTarget | null, scroller: HTMLElement): boolean {
  if (!(target instanceof Element)) return false;
  // A press on the scrollbar itself is not delivered to the element, so it never gets here.
  return !target.closest(INTERACTIVE) && scroller.contains(target);
}

/**
 * Left-button drag on an EMPTY part of the board scrolls it (grab / grabbing cursor). Mouse only:
 * touch keeps its natural scrolling and long-press drag. Cards, columns, handles, controls, links
 * and inputs are never captured (cards and columns are `draggable`, so native drag-and-drop keeps
 * working), a press that does not move stays a click, and text selection is suppressed only while
 * panning. Listeners are removed on release, cancel and unmount.
 */
export function usePanScroll(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const scroller = ref.current;
    if (!scroller) return;
    let start: { x: number; y: number; left: number; id: number } | null = null;
    let panning = false;
    let suppressClick = false;

    const stop = () => {
      if (start && panning && scroller.hasPointerCapture(start.id)) {
        scroller.releasePointerCapture(start.id);
      }
      start = null;
      panning = false;
      scroller.removeAttribute('data-panning');
    };

    const onDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!canStartPan(event.target, scroller)) return;
      start = {
        x: event.clientX,
        y: event.clientY,
        left: scroller.scrollLeft,
        id: event.pointerId,
      };
    };
    const onMove = (event: PointerEvent) => {
      if (!start || event.pointerId !== start.id) return;
      if (event.buttons === 0) {
        stop();
        return;
      }
      const dx = event.clientX - start.x;
      if (!panning) {
        if (Math.hypot(dx, event.clientY - start.y) < PAN_THRESHOLD) return;
        panning = true;
        scroller.setPointerCapture(start.id);
        scroller.setAttribute('data-panning', 'true');
      }
      scroller.scrollLeft = start.left - dx;
      event.preventDefault();
    };
    // After a pan the click that follows the release must not reach whatever lies under it.
    const onClick = (event: MouseEvent) => {
      if (suppressClick) {
        suppressClick = false;
        event.stopPropagation();
        event.preventDefault();
      }
    };
    const onUp = (event: PointerEvent) => {
      if (!start || event.pointerId !== start.id) return;
      suppressClick = panning;
      stop();
      if (suppressClick) window.setTimeout(() => (suppressClick = false), 0);
    };
    const noSelect = (event: Event) => {
      if (panning) event.preventDefault();
    };

    scroller.addEventListener('pointerdown', onDown);
    scroller.addEventListener('pointermove', onMove);
    scroller.addEventListener('pointerup', onUp);
    scroller.addEventListener('pointercancel', stop);
    scroller.addEventListener('lostpointercapture', stop);
    scroller.addEventListener('click', onClick, true);
    scroller.addEventListener('selectstart', noSelect);
    return () => {
      stop();
      scroller.removeEventListener('pointerdown', onDown);
      scroller.removeEventListener('pointermove', onMove);
      scroller.removeEventListener('pointerup', onUp);
      scroller.removeEventListener('pointercancel', stop);
      scroller.removeEventListener('lostpointercapture', stop);
      scroller.removeEventListener('click', onClick, true);
      scroller.removeEventListener('selectstart', noSelect);
    };
  }, [ref]);
}
